// Duplicated from ../container-legacy/, differing only in the name of
// the host interface it probes through. The two backends serve
// different container scheduling policies, but this file touches
// neither the launch spec nor the policy, so there is no divergence
// pressure on it and the copy is deliberate rather than overlooked.
// Fixes here apply to both copies.
//
// Shared computerd health probe.
//
// Used in two places that must agree on what "healthy" means:
//
//   - LegacyContainerBackend.connect() startup readiness, in
//     place of the previous private #waitForPort loop;
//   - the keep-alive alarm's lease-time check.
//
// A single HEAD against the configured healthPath on the container
// port. ctx.container.running tells the runtime the container is
// attached, not that computerd is listening; this probe closes the gap.
//
// Probe failures bubble out as rejections; callers apply their own
// backoff / budget. No retries here — keeping the helper a single
// shot lets startup and lease alarms compose it differently.

import type { IWorkspaceContainerAPI } from "./container-host.js";

export interface ComputerdHealthProbeOptions {
  // TCP port computerd listens on inside the container.
  port: number;
  // Path to probe. Defaults to /health at the call site; required
  // here so both callers thread the same value.
  path: string;
  // Per-probe timeout. The helper aborts the request when it
  // elapses; the host's fetchPort surfaces that as a rejection.
  timeoutMs: number;
}

export async function probeComputerdHealth(
  host: IWorkspaceContainerAPI,
  options: ComputerdHealthProbeOptions,
): Promise<void> {
  const signal = AbortSignal.timeout(options.timeoutMs);
  const res = await host.fetchPort(options.port, `http://container${options.path}`, {
    method: "HEAD",
    signal,
  });
  // Drain the body so the underlying connection (if any) can be
  // released. Some runtimes return a non-null body for HEAD even
  // though it should be empty; cancel() is a no-op for null.
  void res.body?.cancel();
  if (!res.ok) {
    throw new Error(`computerd health returned ${res.status}`);
  }
}
